import React, { useContext, useEffect, useState } from 'react'
import {
	FormBuilder,
	formKeySelector,
	updateControlState,
	validateFormDataAction,
} from 'formBuilder/src'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import {
	ApplicationDispatchContext,
	LOAN_APPLICATION_ACTIONS,
} from 'contexts/ApplicationContext'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { getLanguageLocale, getMarket } from 'utils/countryHelpers'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { FormFactory } from 'formBuilder/formFactory'
import { CustomerForm } from 'constants/enums/formTypes'

import './FillPersonalDetail.scss'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import updateApplicationServices from 'services/updateApplicationServices'
import getApplicationServices from 'services/getApplicationServices'
import { LifeCycleStatus } from 'constants/enums/lifeCycleStatus'
import { AllCountries } from 'constants/enums/countryTypes'
import {
	IKycCurrencyAndValues,
	kycCurrenciesAndValues,
} from 'constants/values/kycValues'
import { OccupationType } from 'constants/enums/occupationTypes'
import { IUpdateApplicationInformation } from 'interfaces/i-update-application'

function validateGermanIbanChecksum(iban: string): boolean {
	const upper = iban.toUpperCase()
	const rearranged = upper.slice(4) + upper.slice(0, 4)
	const numeric = rearranged.replace(/[A-Z]/g, (c) =>
		(c.charCodeAt(0) - 55).toString(),
	)
	let remainder = 0
	for (const char of numeric) {
		remainder = (remainder * 10 + parseInt(char, 10)) % 97
	}
	return remainder === 1
}

export const FillPersonalDataDe = () => {
	const { t } = useTranslation()
	const { loanApplication, dispatchLoanApplication }: IApplicationContext =
		useContext(ApplicationDispatchContext)
	const { msp, dispatchMsp } = useContext(MspDispatchContext)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<Error | any>()

	const dispatch = useDispatch()

	let applicationInfoFormSchema = FormFactory(
		CustomerForm.ApplicationUpdateForm,
		{
			formName: 'applicationUpdateForm',
			blockName: 'applicationUpdateForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
			submitButtonText: t('Submit'),
		},
		sanitizeApplicationData(
			loanApplication.applicationData as IApplicationInformation,
		),
	)

	// set fields to null if the enum values we received aren't valid för Germany
	function sanitizeApplicationData(
		data: IApplicationInformation,
	): IApplicationInformation {
		let germanKycData = kycCurrenciesAndValues.find(
			(e) => e.country === 'de',
		) as IKycCurrencyAndValues

		if (
			data.personalInfo?.maritalStatus &&
			!germanKycData.maritalStatus?.includes(
				data.personalInfo?.maritalStatus,
			)
		) {
			data.personalInfo.maritalStatus = null
		}

		let germanOccupations = germanKycData.occupation?.map(
			(occupation: number) => OccupationType[occupation],
		) // needs special treatment as OccupationType is a numeric and not a string enum
		if (
			data.employment?.occupationType &&
			germanOccupations &&
			!germanOccupations.includes(
				data.employment?.occupationType.toString(),
			)
		) {
			data.employment.occupationType = null
		}

		if (
			data.accommodation?.type &&
			!germanKycData.accommodationType?.includes(data.accommodation?.type)
		) {
			data.accommodation.type = null
		}

		return data
	}

	const applicationInfoFormStateValues = useSelector(
		formKeySelector(
			applicationInfoFormSchema.formMeta.formName,
			'controlsState',
		),
	)

	useEffect(
		() => {
			dispatchLoanApplication({
				type: LOAN_APPLICATION_ACTIONS.SET_UPDATE_APPLICATIONDATA,
				payload: {
					...loanApplication,
					updateApplicationData: {
						...loanApplication.updateApplicationData,
						applicant: {
							...loanApplication.updateApplicationData?.applicant,
							personalInfo: {
								...loanApplication.updateApplicationData
									?.applicant?.personalInfo,
								maritalStatus:
									applicationInfoFormStateValues
										?.maritalStatus.value,
								cityOfBirth:
									applicationInfoFormStateValues?.cityOfBirth
										.value,
								countryOfBirth:
									applicationInfoFormStateValues
										?.countryOfBirth.value,
								nationality:
									applicationInfoFormStateValues?.nationality
										.value,
								citizenship: [
									applicationInfoFormStateValues?.nationality
										.value,
								], // the nationality field in the form should populate the citizenship value as well
								inCountrySince: applicationInfoFormStateValues
									?.inCountrySince.visible
									? applicationInfoFormStateValues
											?.inCountrySince.value
									: null,
								residencePermit: applicationInfoFormStateValues
									?.residencePermit.visible
									? applicationInfoFormStateValues
											?.residencePermit.value
									: null,
								residencePermitUntil:
									applicationInfoFormStateValues
										?.residencePermitUntil.visible
										? applicationInfoFormStateValues
												?.residencePermitUntil.value
										: null,
							},
							address: {
								...loanApplication.updateApplicationData
									?.applicant?.address,
								countryOfResidence:
									'DE' as unknown as AllCountries, // should always be DE
							},
							accommodation: {
								...loanApplication.updateApplicationData
									?.applicant.accommodation,
								since: applicationInfoFormStateValues
									?.accomodationSince.value,
								childrenInFamily:
									applicationInfoFormStateValues
										?.childrenInFamily.value,
								type: applicationInfoFormStateValues
									?.accomodationType.value,
							},
							employment: {
								...loanApplication.updateApplicationData
									?.applicant.employment,
								occupationType:
									applicationInfoFormStateValues
										?.occupationType.value,
								isOnProbation: applicationInfoFormStateValues
									?.onProbation.visible
									? applicationInfoFormStateValues
											?.onProbation.value
									: null,
								employedSince: applicationInfoFormStateValues
									?.employedSince.visible
									? applicationInfoFormStateValues
											?.employedSince.value
									: null,
								employedUntil: applicationInfoFormStateValues
									?.employedTo.visible
									? applicationInfoFormStateValues?.employedTo
											.value
									: null,
							},
							expenses: {
								warmRent: applicationInfoFormStateValues
									?.warmRent.visible
									? applicationInfoFormStateValues?.warmRent
											.value
									: null,
							},
							additionalInfo: {
								...loanApplication.updateApplicationData
									?.applicant?.additionalInfo,
								beneficialOwner:
									applicationInfoFormStateValues
										?.beneficialOwner.value,
							},
						},
						bankInfo: {
							...loanApplication.updateApplicationData?.bankInfo,
							iban: applicationInfoFormStateValues?.ibanVal.value?.toUpperCase(),
						},
						purposeOfLoan:
							applicationInfoFormStateValues?.purposeOfLoan.value,
					},
				},
			})
		},
		[applicationInfoFormStateValues], // eslint-disable-line react-hooks/exhaustive-deps
	)

	const ibanValue = applicationInfoFormStateValues?.ibanVal?.value as
		| string
		| undefined

	useEffect(() => {
		if (!ibanValue) return
		if (!/^(d|D)(e|E)([0-9]{20})$/.test(ibanValue)) return
		if (!validateGermanIbanChecksum(ibanValue)) {
			dispatch(
				updateControlState({
					formName: 'applicationUpdateForm',
					controlName: 'ibanVal',
					isValid: false,
					validationMessage: t('InvalidIban'),
				}),
			)
		}
	}, [ibanValue]) // eslint-disable-line react-hooks/exhaustive-deps

	function addFixedValuesToApplicationData(
		data: IUpdateApplicationInformation,
	): IUpdateApplicationInformation {
		if (loanApplication.applicationData?.loanNumber)
			data.applicationId = loanApplication.applicationData?.loanNumber
		if (loanApplication.applicationData?.product)
			data.product = loanApplication.applicationData?.product
		if (loanApplication.applicationData?.approvedRepaymentPeriod)
			data.repaymentPeriod =
				loanApplication.applicationData?.approvedRepaymentPeriod

		return data
	}

	const submitUpdatedApplicationData = async () => {
		dispatch(validateFormDataAction('applicationUpdateForm'))
		if (!loanApplication.updateApplicationData) return

		const ibanToValidate =
			loanApplication.updateApplicationData?.bankInfo?.iban
		if (
			ibanToValidate &&
			/^(d|D)(e|E)([0-9]{20})$/.test(ibanToValidate) &&
			!validateGermanIbanChecksum(ibanToValidate)
		) {
			dispatch(
				updateControlState({
					formName: 'applicationUpdateForm',
					controlName: 'ibanVal',
					isValid: false,
					validationMessage: t('InvalidIban'),
				}),
			)
			return
		}

		let updatedData = addFixedValuesToApplicationData(
			loanApplication.updateApplicationData,
		)

		setLoading(true)
		setError(null)
		try {
			await updateApplicationServices
				.updateLoanApplicationDe(updatedData, t)
				.then(async (response) => {
					if (response.status === 200) {
						const appInfo =
							await getApplicationServices.GetApplicationInformation(
								loanApplication?.applicationData?.loanNumber!,
								t,
							)
						loanApplication.setApplicationData(appInfo)
						const stepsToSkip =
							appInfo?.lifeCycleStatus ===
							LifeCycleStatus.WaitingForBankData
								? 1
								: 2
						dispatchMsp({
							type: MSP_ACTIONS.SET_INDEX,
							payload: {
								index: msp.index + stepsToSkip,
							},
						})
					}
				})
		} catch (error) {
			console.warn(error)
			setError(error)
			setLoading(false)
		}
	}

	return (
		<div className="update-personal-data">
			{loading ? (
				<div className="col-spinner">
					<CircleSpinner />
				</div>
			) : (
				<>
					{error ? (
						<ErrorBox message={t('ErrorMsg')} />
					) : (
						<>
							<div className="update-personal-data-section">
								{applicationInfoFormSchema ? (
									<FormBuilder
										formSchema={applicationInfoFormSchema}
										apiError={undefined}
										showErrorsInForm={true}
										showSubmitButton={true}
										onSubmit={() => {
											setTimeout(() => {
												submitUpdatedApplicationData()
											}, 400)
										}}
									/>
								) : (
									<CircleSpinner />
								)}
							</div>
						</>
					)}
				</>
			)}
		</div>
	)
}